import React from 'react';

import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import FormContainer from '../../../FormContainer';
import Table from '../../../../Table';
import Pagination from '../../../../Pagination';
import AddButton from '../../../../AddButton';
import CreatePrincipalPayment from './CreatePrincipalPayment';
import DemandAlert from '../../DemandAlert';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';
import InvoicesCell from './InvoicesCell';
import AchAlert from './AchAlert';
import Alert from '../../../../Alert';

import usePaymentsForm from '../../../../../forms/loan/payments';
import PaymentService from '../../../../../services/PaymentService';
import { useUiContext } from '../../../../../context/UiContext';
import formatCurrency from '../../../../../helpers/formatCurrency';
import LoanService from '../../../../../services/LoanService';
// import { createAchCheckPaymentStatus } from '../../../../../helpers/createAchCheckStatus';
import BorrowerService from '../../../../../services/BorrowerService';
import { useServicerStore } from '../../../../../store';
import usePagination from '../../../../../hooks/usePagination';
import useQueryParam from '../../../../../hooks/useQueryParam';
import LenderService from '../../../../../services/LenderService';
import createPaymentAchCheckStatus from '../../../../../helpers/tables/createPaymentAchCheckStatus';

const tabs = [
  {
    label: 'Payments',
  },
  {
    label: 'Demands',
  },
];

const createBankAccountValue = (
  values,
  primaryBankAccountId,
  bankAccounts,
  lender
) => {
  let bankAccountId = null;

  if (values.source !== 'ACH') {
    return bankAccountId;
  }

  if (values?.payer === 'Borrower' && bankAccounts && bankAccounts.length > 1) {
    bankAccountId = values?.bank_account_id;
  } else if (
    values?.payer === 'Borrower' &&
    bankAccounts &&
    bankAccounts.length === 1
  ) {
    bankAccountId = primaryBankAccountId;
  } else if (values.source === 'ACH' && values.payer === 'Servicer') {
    bankAccountId = values?.bank_account_id;
  } else if (values.source === 'ACH' && values.payer === 'Lender') {
    bankAccountId = lender?._bank_account?.id;
  }

  return bankAccountId;
};

export default function Payments({
  isVisible,
  isFetching,
  loan,
  refetchLoan,
  readOnly,
  loanId,
  isLender,
}) {
  const { showToast } = useUiContext();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const userRole = useServicerStore((state) => state.userRole);
  const isBorrower = userRole === 'borrower';

  const { data: payments, refetch } = useQuery({
    queryKey: ['payments', loanId],
    queryFn: () => LoanService.getLoanPayments({ loanId }),
    enabled: !!loanId && !isLender,
  });

  const { addQueryParam, getQueryParam } = useQueryParam('addNew');

  const isAddNew = getQueryParam();

  const {
    currentPage,
    switchToPage,
    pageTotal,
    isFetching: areLenderPaymentsFetching,
    data,
  } = usePagination({
    queryKey: ['payments', loanId],
    queryFn: LoanService.getLoanPayments,
    queryFnArgs: { loanId },
  });

  const primaryBorrower = loan?.borrowers?.find(
    (borrower) => borrower.position === 'primary'
  );

  const { data: borrower } = useQuery({
    queryKey: ['borrower', primaryBorrower?.borrower_id],
    queryFn: () => BorrowerService.getBorrower(primaryBorrower.borrower_id),
    enabled: !!primaryBorrower,
  });

  const { data: lender } = useQuery({
    queryKey: ['lender', loan?.lender_id],
    queryFn: () => LenderService.getLender(loan?.lender_id),
    enabled: !!loan?.lender_id,
  });

  const {
    getQueryParam: getPrincipalPaymentQueryParam,
    addQueryParam: addPrincipalPaymentQueryParam,
  } = useQueryParam('addNewPrincipalPayment');
  const isAddNewPrincipalPayment = getPrincipalPaymentQueryParam();

  const isAllowedToAddPrincipalPayment =
    !data?._payments?.some((payment) => payment.status === 'unapplied') &&
    payments?.every?.((payment) => {
      return payment.status === 'applied';
    });

  const cols = [
    {
      title: t('common.tables.columns.Date'),
      keys: ['date'],
      format: (value) => moment(value).format('M/D/YY'),
      link: (row) => `/loans/${loan?.id}/payments/${row.id}?tab=Payment`,
    },
    {
      title: t('common.tables.columns.Source'),
      keys: ['source'],
    },
    {
      title: t('common.tables.columns.Reference'),
      keys: ['reference'],
    },
    {
      title: t('common.tables.columns.Amount'),
      keys: ['amount'],
      format: (value) => {
        return `$${formatCurrency(value)}`;
      },
    },
    {
      title: t('common.tables.columns.Applied'),
      keys: ['status'],
      type:
        readOnly || isBorrower || isLender
          ? ''
          : (row) => {
              return row.status === 'void' ? 'text' : 'button';
            },
      buttonVariant: (row) => {
        return row.status !== 'applied' ? '' : 'transparent';
      },
      label: (row) => {
        const defaultAmount = `$${formatCurrency(row?._total?.total_paid)}`;
        const appliedAmount = `$${formatCurrency(
          (row.amount ?? 0) - (row._total?.total_paid ?? 0)
        )}`;

        if (row.status === 'applied') {
          return defaultAmount;
        }

        if (row.status === 'partial') {
          return appliedAmount;
        }

        if (row.status === 'unapplied') {
          return t('common.Apply');
        }

        if (row.status === 'void') {
          return t('common.Void');
        }

        return defaultAmount;
      },
      onClick: (row) =>
        navigate(`/loans/${row.loan_id}/payments/${row.id}?tab=Payment`),
    },
    {
      title: t('common.tables.columns.Billed'),
      keys: ['_distributed.total_billed'],
      type: 'currency',
    },
    {
      title: t('common.tables.columns.ACH/Check'),
      keys: ['billcom_receivedpay_status'],
      format: (value, row) => {
        return createPaymentAchCheckStatus(row);
      },
      type: 'badge',
      badgeColor: (value) => {
        switch (value) {
          case 'Not started':
            return 'amber';
          case 'paid':
          case 'Paid':
          case 'cleared':
          case 'Cleared':
          case 'Confirmed':
          case 'Deposited':
            return 'emerald';
          case 'scheduled':
          case 'Scheduled':
          case 'Pending':
            return 'yellow';
          case 'void':
          case 'Void':
          case 'cancelled':
          case 'Cancelled':
          case 'Escheated':
            return 'rose';
          default:
            return 'slate';
        }
      },
    },

    {
      title: t('common.tables.columns.Invoices'),
      keys: ['_invoices'],
      type: 'component',
      component: InvoicesCell,
      props: (row) => ({
        row,
        loan,
        isBorrower,
      }),
    },
  ];

  const currentCols = isBorrower
    ? cols.filter((col) => col.title !== t('common.tables.columns.Billed'))
    : cols;

  let paymentForm;

  const { mutate: createPayment, isPending } = useMutation({
    mutationFn: (formValues) => {
      return PaymentService.createPayment({
        formValues: {
          ...formValues,
          bank_account_id: createBankAccountValue(
            formValues,
            loan?.bank_account_id,
            borrower?._bank_accounts,
            lender
          ),
        },
        loanId: loan.id,
      });
    },
    onSuccess: (payment) => {
      showToast({
        text: 'Payment created',
        type: 'success',
      });
      refetchLoan();
      refetch();
      paymentForm.formik.resetForm();
      navigate(`/loans/${loan.id}/payments/${payment.id}?tab=Payment`);
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  paymentForm = usePaymentsForm(
    createPayment,
    null,
    loan?.bank_account_id,
    borrower?._bank_accounts,
    loan?.id
  );

  let paymentsSource = isBorrower ? payments : payments;

  if (isLender) {
    paymentsSource = data?.items;
  }

  // Sort payments by created_at DESC (Most recent payments first)
  const sortedPayments = paymentsSource?.sort?.((a, b) => {
    return new Date(b.date) - new Date(a.date);
  });

  return (
    <div>
      <div className="mb-4 flex justify-between md:items-center flex-col md:flex-row gap-2">
        <AddOrEditPageTabs tabs={tabs} />
        <div className="flex sm:flex-row flex-col gap-2">
          {!isAddNewPrincipalPayment && isAllowedToAddPrincipalPayment && (
            <AddButton onClick={addPrincipalPaymentQueryParam} noShrink>
              Principal Payment
            </AddButton>
          )}
          <AddButton
            style={{
              visibility:
                isAddNew ||
                readOnly ||
                isBorrower ||
                isLender ||
                isAddNewPrincipalPayment
                  ? 'hidden'
                  : 'visible',
            }}
            onClick={addQueryParam}
            noShrink
          >
            Create Payment
          </AddButton>
        </div>
      </div>
      <div className="p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        {isBorrower && (
          <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-6 flex justify-between w-full">
            Payments
          </h2>
        )}

        {!isAddNew && <DemandAlert loanId={loanId} />}
        {loan && loan?.status !== 'active' && (
          <div className="mb-5">
            <Alert message="This loan is not active. Payments created for this loan will not be run until the loan is activated." />
          </div>
        )}

        {!readOnly && !isBorrower && !isLender && isAddNew && (
          <div className="mb-10">
            <FormContainer
              form={paymentForm}
              isVisible={isVisible}
              noBillComId
              isSubmitting={isPending}
            />
            <AchAlert loan={loan} paymentValues={paymentForm?.formik?.values} />
          </div>
        )}
        {!isAddNew && !isAddNewPrincipalPayment && (
          <>
            <Table
              cols={currentCols}
              rows={sortedPayments}
              title="Payments"
              isFetching={isLender ? areLenderPaymentsFetching : isFetching}
              minHeight={565}
            />
            {isLender && (
              <Pagination
                pageTotal={pageTotal}
                switchToPage={switchToPage}
                currentPage={currentPage}
              />
            )}
          </>
        )}
        {isAddNewPrincipalPayment && (
          <CreatePrincipalPayment
            refetch={refetch}
            loanId={loanId}
            loan={loan}
          />
        )}
      </div>
    </div>
  );
}
